import {
    Autocomplete,
    Box,
    Button, Checkbox, Dialog, DialogContent, Divider,
    Fade, FormControlLabel,
    Grid, IconButton,
    Paper,
    Popper,
    Stack,
    TextField,
    Typography,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import React from "react";
import { observer } from "mobx-react";
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PetsIcon from '@mui/icons-material/Pets';
import { DateRangePicker } from "app/modules/common/date-range-picker/date-range-picker";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewListIcon from '@mui/icons-material/ViewList';
import MapIcon from '@mui/icons-material/Map';
import { grey } from "@mui/material/colors";
import { SearchCitiesResult } from "app/modules/main/api/main-clients.api";
import { DebounceInput } from "app/modules/main/components/common/debounce-input";
import { LocationRenderOption } from "app/modules/main/components/main/search/location-render";
import { useInstance } from "react-ioc";
import { SearchStore } from "app/modules/main/components/main/search/SearchStore";
import { BoxCenter } from "app/modules/common/layout/components/containers";
import ClickAwayListener from "@mui/material/ClickAwayListener";

import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { runInAction } from "mobx";
import { NumericFormatCustom } from "app/modules/common/details/text-field";
import { MainStore } from "app/modules/main/stores/MainStore";

function GuestField(props: { label: string, value: number, disableMinus: boolean, increase: () => void, decrease: () => void }) {
    return <Stack direction={"row"} sx={{ p: 1 }}>
        <BoxCenter>
            <Typography sx={{ width: "80px", fontSize: "14px" }}>{props.label}</Typography>
        </BoxCenter>
        <IconButton disabled={props.disableMinus} onClick={props.decrease}>
            <RemoveIcon />
        </IconButton>
        <BoxCenter>
            <Typography sx={{ fontWeight: props.value > 0 ? "bold" : "normal" }}>{props.value}</Typography>
        </BoxCenter>
        <IconButton onClick={props.increase}>
            <AddIcon />
        </IconButton>
    </Stack>;
}

export default observer(() => {
    const [anchorElGuestPopup, setAnchorElGuestPopup] = React.useState(null);
    const [openGuestPopup, setOpenGuestPopup] = React.useState(false);

    const [openFilterPopup, setOpenFilterPopup] = React.useState(false);

    const store = useInstance(SearchStore);
    const mainStore = useInstance(MainStore);

    const filterOptions = (options: SearchCitiesResult[]): SearchCitiesResult[] => options;

    const controlHeight = "44px";
    const controlRadius = "8px";

    let location = <Autocomplete
        className="SearchHeaderInputs"
        options={store.searchCitiesResult}
        value={store.searchValue}
        sx={{ minWidth: "220px" }}
        onChange={(event: unknown, value: SearchCitiesResult | null) => store.setSearchValue(value)}
        getOptionLabel={(item: SearchCitiesResult) => `${item.city}, ${item.state}`}
        renderInput={(params) => <DebounceInput
            placeholder="Type in here…"
            debounceTimeout={400}
            handleDebounce={(value: string) => {
                store.setSearchString(value);
                store.searchCities();
            }}
            {...params}
        />}
        renderOption={LocationRenderOption}
        loading={store.isLoading}
        filterOptions={filterOptions}
        blurOnSelect={true}
    />;

    let dateRange = <Box sx={{ width: { sm: "100%", md: "auto" } }}>
        <DateRangePicker
            textFieldClassName="SearchHeaderInputs"
            disablePast
            value={store.dates}
            onAccept={(newValue) => {
                store.setDates(newValue);
            }}
            textFieldSx={{ width: "100%", minWidth: "220px !important" }}
        />
    </Box>;

    let guests = <BoxCenter sx={{ borderColor: grey[300], borderWidth: "1px", borderStyle: "solid", borderRadius: controlRadius, cursor: "pointer", height: controlHeight, width: { xs: "70%", sm: "70%", md: "auto" }, backgroundColor: "#fff", "&:hover": { borderColor: grey[500] }, transition: "border-color 0.2s" }}
        onClick={(e) => {
            store.copyGuestEdit();
            setAnchorElGuestPopup(e.currentTarget);
            setOpenFilterPopup(false);
            setOpenGuestPopup((prev) => !prev);
        }}>
        <Stack direction={"row"} sx={{ color: grey[700], px: 1.5, fontSize: "14px" }} spacing={2}>
            <Stack direction={"row"} spacing={0.75} alignItems={"center"}>
                <PeopleAltIcon sx={{ fontSize: 20 }} />
                <BoxCenter>{store.searchParams.guestsAdults + store.searchParams.guestsChildren} guest{(store.searchParams.guestsAdults + store.searchParams.guestsChildren) > 1 ? "s" : ""}</BoxCenter>
            </Stack>
            <Stack direction={"row"} spacing={0.75} alignItems={"center"}>
                <PetsIcon sx={{ fontSize: 20 }} />
                <BoxCenter>{store.searchParams.guestsPets} pet{(store.searchParams.guestsPets) == 1 ? "" : "s"}</BoxCenter>
            </Stack>
        </Stack>
    </BoxCenter>;

    let onFilterClick = () => {
        store.copyFilterEdit();
        setOpenGuestPopup(false);
        setOpenFilterPopup(true);
    };

    let filter = <Button variant="outlined"
        startIcon={<FilterAltIcon sx={{ fontSize: 20 }} />}
        sx={{ width: "30%", height: controlHeight, borderRadius: controlRadius, color: grey[600], borderColor: grey[300], textTransform: "none", fontSize: "14px" }}
        onClick={onFilterClick}>
        Filters
    </Button>;

    let viewSwitcher = <ToggleButtonGroup
        value={store.viewType}
        exclusive
        onChange={() => store.toggleViewType()}
        sx={{ height: controlHeight, borderRadius: controlRadius, overflow: "hidden" }}
    >
        <ToggleButton value="table" sx={{ px: 2, textTransform: "none", fontSize: "14px", borderRadius: `${controlRadius} 0 0 ${controlRadius}` }}>
            <ViewListIcon sx={{ mr: 0.5, fontSize: 20 }} />
            List
        </ToggleButton>
        <ToggleButton value="map" sx={{ px: 2, textTransform: "none", fontSize: "14px", borderRadius: `0 ${controlRadius} ${controlRadius} 0` }}>
            <MapIcon sx={{ mr: 0.5, fontSize: 20 }} />
            Map
        </ToggleButton>
    </ToggleButtonGroup>;

    let filterFullView = <Button variant="outlined" startIcon={<FilterAltIcon sx={{ fontSize: 20 }} />} sx={{ height: controlHeight, borderRadius: controlRadius, color: grey[600], borderColor: grey[300], minWidth: "120px", textTransform: "none", fontSize: "14px", "&:hover": { borderColor: grey[500], backgroundColor: grey[50] } }} onClick={onFilterClick}>
        Filters
    </Button>;

    let PopupBodyGuests = observer(({ ...props }) => <ClickAwayListener onClickAway={(e) => { setOpenGuestPopup(false); e.preventDefault(); }}>
        <Fade {...props} timeout={350}>
            <Paper sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", overflow: "hidden", mt: 1, minWidth: "260px" }}>
                <Typography sx={{ fontWeight: 600, fontSize: "15px", px: 2, pt: 2, pb: 1 }}>Guests</Typography>
                <Stack direction={"column"} sx={{ px: 1 }}>
                    <GuestField
                        label={"Adults"}
                        value={store.guestsEdit.adults}
                        disableMinus={store.guestsEdit.adults <= 1}
                        increase={() => runInAction(() => ++store.guestsEdit.adults)}
                        decrease={() => runInAction(() => --store.guestsEdit.adults)} />
                    <GuestField
                        label={"Children"}
                        value={store.guestsEdit.children}
                        disableMinus={store.guestsEdit.children <= 0}
                        increase={() => runInAction(() => ++store.guestsEdit.children)}
                        decrease={() => runInAction(() => --store.guestsEdit.children)} />
                    <GuestField label={"Pets"}
                        value={store.guestsEdit.pets}
                        disableMinus={store.guestsEdit.pets <= 0}
                        increase={() => runInAction(() => ++store.guestsEdit.pets)}
                        decrease={() => runInAction(() => --store.guestsEdit.pets)} />
                </Stack>
                <Divider />
                <Stack direction={"row"} sx={{ p: 1 }}>
                    <Button variant="text" sx={{ width: "50%", color: grey[500], textTransform: "none", borderRadius: 2 }} onClick={() => store.resetGuestEdit()}>Clear</Button>
                    <Button variant="contained" disableElevation sx={{ width: "50%", textTransform: "none", borderRadius: 2 }} onClick={() => {
                        setOpenGuestPopup(false);
                        store.guestsUpdate();
                    }}>Apply</Button>
                </Stack>
            </Paper>
        </Fade>
    </ClickAwayListener>);

    let FilterModal = observer(() => <Dialog
        open={openFilterPopup}
        onClose={() => setOpenFilterPopup(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "85vh" } }}
    >
        {/* Header */}
        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{ px: 3, py: 2, borderBottom: `1px solid ${grey[200]}` }}>
            <Typography sx={{ fontWeight: 700, fontSize: "20px" }}>Filters</Typography>
            <IconButton onClick={() => setOpenFilterPopup(false)} size="small">
                <CloseIcon />
            </IconButton>
        </Stack>

        {/* Content */}
        <DialogContent sx={{ p: 0 }}>
            <Grid container>
                {/* Left column: Rooms & Nightly rate */}
                <Grid item xs={12} md={4} sx={{ p: 3, borderRight: { md: `1px solid ${grey[200]}` } }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "16px", mb: 2 }}>Rooms</Typography>
                    <Stack spacing={2}>
                        <Stack direction={"row"} alignItems={"center"}>
                            <Typography sx={{ width: "100px", fontSize: "14px", color: grey[700] }}>Bedrooms</Typography>
                            <TextField
                                value={(store.filterEdit.minBedrooms || '')}
                                InputProps={{ inputComponent: NumericFormatCustom as any, sx: { height: "40px", borderRadius: 2 } }}
                                inputProps={{ prefix: "", style: { textAlign: 'center' } }}
                                onChange={(e) => store.updateMinBedrooms(Math.abs(parseFloat(e.target.value)))}
                                placeholder={"min"}
                                autoComplete={"off"}
                                variant="outlined"
                                size="small"
                                sx={{ width: "80px" }}
                            />
                            <Typography sx={{ px: 1, color: grey[400] }}>-</Typography>
                            <TextField
                                value={(store.filterEdit.maxBedrooms || '')}
                                InputProps={{ inputComponent: NumericFormatCustom as any, sx: { height: "40px", borderRadius: 2 } }}
                                inputProps={{ prefix: "", style: { textAlign: 'center' } }}
                                onChange={(e) => store.updateMaxBedrooms(Math.abs(parseFloat(e.target.value)))}
                                placeholder={"max"}
                                autoComplete={"off"}
                                variant="outlined"
                                size="small"
                                sx={{ width: "80px" }}
                            />
                        </Stack>
                        <Stack direction={"row"} alignItems={"center"}>
                            <Typography sx={{ width: "100px", fontSize: "14px", color: grey[700] }}>Bathrooms</Typography>
                            <TextField
                                value={(store.filterEdit.minBathrooms || '')}
                                InputProps={{ inputComponent: NumericFormatCustom as any, sx: { height: "40px", borderRadius: 2 } }}
                                inputProps={{ prefix: "", style: { textAlign: 'center' } }}
                                onChange={(e) => store.updateMinBathrooms(Math.abs(parseFloat(e.target.value)))}
                                placeholder={"min"}
                                autoComplete={"off"}
                                variant="outlined"
                                size="small"
                                sx={{ width: "80px" }}
                            />
                            <Typography sx={{ px: 1, color: grey[400] }}>-</Typography>
                            <TextField
                                value={(store.filterEdit.maxBathrooms || '')}
                                InputProps={{ inputComponent: NumericFormatCustom as any, sx: { height: "40px", borderRadius: 2 } }}
                                inputProps={{ prefix: "", style: { textAlign: 'center' } }}
                                onChange={(e) => store.updateMaxBathrooms(Math.abs(parseFloat(e.target.value)))}
                                placeholder={"max"}
                                autoComplete={"off"}
                                variant="outlined"
                                size="small"
                                sx={{ width: "80px" }}
                            />
                        </Stack>
                    </Stack>

                    <Divider sx={{ my: 3 }} />

                    <Typography sx={{ fontWeight: 600, fontSize: "16px", mb: 2 }}>Nightly rate</Typography>
                    <Stack direction={"row"} alignItems={"center"}>
                        <TextField
                            value={(store.filterEdit.minRate || '')}
                            InputProps={{ inputComponent: NumericFormatCustom as any, sx: { height: "40px", borderRadius: 2 } }}
                            inputProps={{ prefix: "$", style: { textAlign: 'center' } }}
                            onChange={(e) => store.updateMinRate(Math.abs(parseFloat(e.target.value)))}
                            placeholder={"$ min"}
                            autoComplete={"off"}
                            variant="outlined"
                            size="small"
                            sx={{ flex: 1 }}
                        />
                        <Typography sx={{ px: 1, color: grey[400] }}>-</Typography>
                        <TextField
                            value={(store.filterEdit.maxRate || '')}
                            InputProps={{ inputComponent: NumericFormatCustom as any, sx: { height: "40px", borderRadius: 2 } }}
                            inputProps={{ prefix: "$", style: { textAlign: 'center' } }}
                            onChange={(e) => store.updateMaxRate(Math.abs(parseFloat(e.target.value)))}
                            placeholder={"$ max"}
                            autoComplete={"off"}
                            variant="outlined"
                            size="small"
                            sx={{ flex: 1 }}
                        />
                    </Stack>
                </Grid>

                {/* Middle column: Property type */}
                <Grid item xs={12} md={3} sx={{ p: 3, borderRight: { md: `1px solid ${grey[200]}` }, borderTop: { xs: `1px solid ${grey[200]}`, md: "none" } }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "16px", mb: 2 }}>Property type</Typography>
                    <Stack spacing={0.5}>
                        {mainStore.clientData.propertyTypes.map(x =>
                            <FormControlLabel
                                key={x.id}
                                control={<Checkbox sx={{ padding: "6px" }} size="small" checked={store.filterEdit.propertyTypes.indexOf(x.id) >= 0} onClick={() => store.checkPropertyType(x.id)} />}
                                label={<Typography sx={{ fontSize: "14px" }}>{x.name}</Typography>}
                            />
                        )}
                    </Stack>
                </Grid>

                {/* Right column: Amenities */}
                <Grid item xs={12} md={5} sx={{ p: 3, borderTop: { xs: `1px solid ${grey[200]}`, md: "none" } }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "16px", mb: 2 }}>Amenities</Typography>
                    <Grid container>
                        {mainStore.clientData.amenities.map(x => <Grid item key={x.id} xs={6}>
                            <FormControlLabel
                                control={<Checkbox sx={{ padding: "6px" }} size="small" checked={store.filterEdit.propertyAmenities.indexOf(x.id) >= 0} onClick={() => store.checkAmenity(x.id)} />}
                                label={<Typography sx={{ fontSize: "13px" }}>{x.name}</Typography>} />
                        </Grid>)}
                    </Grid>
                </Grid>
            </Grid>
        </DialogContent>

        {/* Footer */}
        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} sx={{ px: 3, py: 2, borderTop: `1px solid ${grey[200]}` }}>
            <Button variant="text" sx={{ color: grey[600], textTransform: "none", fontWeight: 500 }} onClick={() => runInAction(() => store.resetFilterEdit())}>Clear all</Button>
            <Button variant="contained" disableElevation sx={{ textTransform: "none", borderRadius: 2, px: 4, py: 1 }} onClick={() => {
                setOpenFilterPopup(false);
                store.filterUpdate();
            }}>Show results</Button>
        </Stack>
    </Dialog>);

    return (<Grid container>
        <Grid item xs={12} sx={{ display: { xs: "block", md: "none" } }}>
            <Stack direction={"column"}>
                {location}
                {dateRange}
                <Stack direction={"row"}>
                    {guests}
                    {filter}
                </Stack>
                <Stack direction={"row"} sx={{ mt: 1, justifyContent: "center" }}>
                    {viewSwitcher}
                </Stack>
            </Stack>
        </Grid>
        <Grid item xs={12} sx={{ display: { xs: "none", md: "block" } }}>
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{ px: 3, py: 1.5, backgroundColor: grey[50], borderBottom: `1px solid ${grey[200]}` }}>
                <Stack direction={"row"} alignItems={"center"} spacing={1.5} sx={{ flex: 1 }}>
                    {location}
                    {dateRange}
                    {guests}
                    {filterFullView}
                </Stack>
                {viewSwitcher}
            </Stack>
            {openGuestPopup && <Popper open={openGuestPopup} anchorEl={anchorElGuestPopup} placement={"bottom"} transition sx={{ zIndex: 1300 }}>
                {({ TransitionProps }) => <PopupBodyGuests {...TransitionProps} />}
            </Popper>}
            <FilterModal />
        </Grid>
    </Grid>);
})
